import {
    formatPercentage, formatUSDCurrency, formatUSDCurrencyTwoDigits, numeralFormatter,
} from 'ns_libs/formatter';
import { Deal } from 'views/Deals/types';
import { ProFormaModelIds } from 'constants/proForma';
import { IOptionAnyValue } from 'bricks/types';
import { DEVELOPMENT_SOURCE_METRIC } from 'constants/sources';
import { BOEScenario } from './types';
import { BOESlideoutFormValues } from './components/SlideoutForm/types';
import { COST_BASIS_TYPES, EXPENSE_CALCULATION_MODE, LOAN_CALCULATION_OPTIONS } from './components/SlideoutForm/constants';

export type FormatterType = 'formatPercentage' | 'formatUSDCurrency' | 'numeralFormatter';

interface ScenarioRow {
    label: string;
    value: number | string | null;
    formatter?: FormatterType;
    placeholder?: string;
}

interface ScenarioSection {
    title: string[];
    value: (number | string)[];
    formatter: FormatterType[];
    rows: ScenarioRow[][];
    placeholder?: string;
}

interface ScenarioReturnsSection {
    title: string[];
    value: (number | string | null)[];
    target?: number | null;
    formatter: FormatterType[];
    displayMode: 'unstyled' | 'danger' | 'success';
}

export const formatBOEValues = (value: number | null, formatter?: string) => {
    if (value === undefined || value === null) {
        return '-';
    }
    switch (formatter) {
        case 'formatUSDCurrency':
            return formatUSDCurrency(value);
        case 'formatUSDCurrencyTwoDigits':
            return formatUSDCurrencyTwoDigits(value);
        case 'numeralFormatter':
            return numeralFormatter(value);
        case 'formatPercentage':
            return formatPercentage(value * 100, 100, 2);
        default:
            return value;
    }
};
const placeholders = {
    propertySize: {
        title: 'PROPERTY SIZE',
        placeholder: 'Built GSF',
        rows: {
            efficiencyRatio: 'Efficiency ratio',
            netRental: 'Net rentable SF',
        },
    },
    grossPotentialRent: {
        title: 'GROSS POTENTIAL RENT',
        rows: {
            monthlyRentPricePerUnit: { title: 'Monthly rent price (per unit)', placeholder: '/ unit' },
            monthlyRentPricePerSf: { title: 'Monthly rent price (per SF)', placeholder: '/ SF' },
            numberOfUnits: 'Number of units',
            sfPerUnit: 'SF per unit',
        },
    },
    netOperatingIncome: {
        title: 'NET OPERATING INCOME',
        rows: {
            grossPotentialRent: 'Gross potent. rent',
            monthlyExpenseCostPerUnit: 'Monthly expense / unit',
            monthlyExpenseCostPerSf: 'Monthly expense / SF',
            expensesTotal: 'Total annual expenses',
        },
    },
    totalDevelopmentCosts: {
        title: 'TOTAL DEVELOPMENT COSTS',
        rows: {
            acquisitionCosts: 'Acquisition costs',
            hardCostsPerUnit: 'Hard costs (per unit)',
            hardCostsPerSf: 'Hard costs (per SF)',
            hardCostsTotal: 'Hard costs (total)',
            softCostsPerUnit: 'Soft costs (per unit)',
            softCostsPerSf: 'Soft costs (per SF)',
            softCostsTotal: 'Soft costs (total)',
            financingCosts: 'Financing costs',
        },
    },
    loanAndEquity: {
        title: ['LOAN AMOUNT', 'REQUIRED EQUITY'],
        rows: {
            constructionFinancing: 'Construction financing (LTC)',
        },
    },
    returns: {
        returnOnCost: 'RETURN ON COST',
        returnOnEquity: 'RETURN ON EQUITY',
        profitReturn: 'PROFIT RETURN',
    },
};

export const generateScenarioInformation = (BOEScenario: BOEScenario): ScenarioSection[][] => [
    [
        {
            title: [placeholders.propertySize.title],
            value: [Number(BOEScenario.builtSf || 0)],
            formatter: ['numeralFormatter'],
            placeholder: placeholders.propertySize.placeholder,
            rows: [
                [
                    {
                        label: placeholders.propertySize.rows.efficiencyRatio,
                        value: BOEScenario.buildingEfficiency ? Number(BOEScenario.buildingEfficiency) : null,
                        formatter: 'formatPercentage',
                    },
                    {
                        label: placeholders.propertySize.rows.netRental,
                        value: BOEScenario.netRentableSf ? Number(BOEScenario.netRentableSf) : null,
                        formatter: 'numeralFormatter',
                    },
                ],
            ],
        },
        {
            title: [placeholders.grossPotentialRent.title],
            value: [BOEScenario.grossPotentialRent ? Number(BOEScenario.grossPotentialRent) : 0],
            formatter: ['formatUSDCurrency'],
            rows: [
                [
                    {
                        label: placeholders.grossPotentialRent.rows.monthlyRentPricePerUnit.title,
                        value: BOEScenario.monthlyRentPricePerUnit ? Number(BOEScenario.monthlyRentPricePerUnit) : null,
                        formatter: 'formatUSDCurrency',
                        placeholder: placeholders.grossPotentialRent.rows.monthlyRentPricePerUnit.placeholder,
                    },
                    {
                        label: placeholders.grossPotentialRent.rows.monthlyRentPricePerSf.title,
                        value: BOEScenario.monthlyRentPricePerSf ? Number(BOEScenario.monthlyRentPricePerSf) : null,
                        formatter: 'formatUSDCurrency',
                        placeholder: placeholders.grossPotentialRent.rows.monthlyRentPricePerSf.placeholder,
                    },
                    {
                        label: placeholders.grossPotentialRent.rows.numberOfUnits,
                        value: BOEScenario.numberOfUnits ? Number(BOEScenario.numberOfUnits) : null,
                    },
                    {
                        label: placeholders.grossPotentialRent.rows.sfPerUnit,
                        value: BOEScenario.sfPerUnit ? Number(BOEScenario.sfPerUnit) : null,
                    },
                ],
            ],
        },
        {
            title: [placeholders.netOperatingIncome.title],
            value: [BOEScenario.netOperatingIncome ? Number(BOEScenario.netOperatingIncome) : 0],
            formatter: ['formatUSDCurrency'],
            rows: [
                [
                    {
                        label: placeholders.netOperatingIncome.rows.grossPotentialRent,
                        value: BOEScenario.grossPotentialRent ? Number(BOEScenario.grossPotentialRent) : null,
                        formatter: 'formatUSDCurrency',
                    },
                    {
                        label: placeholders.netOperatingIncome.rows.monthlyExpenseCostPerUnit,
                        value: BOEScenario.monthlyExpenseCostPerUnit ? Number(BOEScenario.monthlyExpenseCostPerUnit) : null,
                        formatter: 'formatUSDCurrency',
                    },
                    {
                        label: placeholders.netOperatingIncome.rows.monthlyExpenseCostPerSf,
                        value: BOEScenario.monthlyExpenseCostPerSf ? Number(BOEScenario.monthlyExpenseCostPerSf) : null,
                        formatter: 'formatUSDCurrency',
                    },
                    {
                        label: placeholders.netOperatingIncome.rows.expensesTotal,
                        value: BOEScenario.expensesTotal ? Number(BOEScenario.expensesTotal) : null,
                        formatter: 'formatUSDCurrency',
                    },
                ],
            ],
        },
    ],
    [
        {
            title: [placeholders.totalDevelopmentCosts.title],
            value: [BOEScenario.totalDevelopmentCosts ? Number(BOEScenario.totalDevelopmentCosts) : 0],
            formatter: ['formatUSDCurrency'],
            rows: [
                [
                    {
                        label: placeholders.totalDevelopmentCosts.rows.acquisitionCosts,
                        value: BOEScenario.acquisition ? Number(BOEScenario.acquisition) : null,
                        formatter: 'formatUSDCurrency',
                    },
                    {
                        label: placeholders.totalDevelopmentCosts.rows.hardCostsPerUnit,
                        value: BOEScenario.hardCostsSubdiv === COST_BASIS_TYPES.PER_UNIT && BOEScenario.hardCosts ? Number(BOEScenario.hardCosts) : null,
                        formatter: 'formatUSDCurrency',
                    },
                    {
                        label: placeholders.totalDevelopmentCosts.rows.hardCostsPerSf,
                        value: BOEScenario.hardCostsSubdiv === COST_BASIS_TYPES.PER_SF && BOEScenario.hardCosts ? Number(BOEScenario.hardCosts) : null,
                        formatter: 'formatUSDCurrency',
                    },
                    {
                        label: placeholders.totalDevelopmentCosts.rows.softCostsPerUnit,
                        value: BOEScenario.softCostsSubdiv === COST_BASIS_TYPES.PER_UNIT && BOEScenario.softCosts ? Number(BOEScenario.softCosts) : null,
                        formatter: 'formatUSDCurrency',
                    },
                    {
                        label: placeholders.totalDevelopmentCosts.rows.softCostsPerSf,
                        value: BOEScenario.softCostsSubdiv === COST_BASIS_TYPES.PER_SF && BOEScenario.softCosts ? Number(BOEScenario.softCosts) : null,
                        formatter: 'formatUSDCurrency',
                    },
                    {
                        label: placeholders.totalDevelopmentCosts.rows.softCostsTotal,
                        value: BOEScenario.softCostsSubdiv === COST_BASIS_TYPES.TOTAL && BOEScenario.softCosts ? Number(BOEScenario.softCosts) : null,
                        formatter: 'formatUSDCurrency',
                    },
                    {
                        label: placeholders.totalDevelopmentCosts.rows.hardCostsTotal,
                        value: BOEScenario.hardCostsSubdiv === COST_BASIS_TYPES.TOTAL && BOEScenario.hardCosts ? Number(BOEScenario.hardCosts) : null,
                        formatter: 'formatUSDCurrency',
                    },
                    {
                        label: placeholders.totalDevelopmentCosts.rows.financingCosts,
                        value: BOEScenario.interestReserveTotal ? Number(BOEScenario.interestReserveTotal) : null,
                        formatter: 'formatUSDCurrency',
                    },
                ],
            ],
        },
        {
            title: placeholders.loanAndEquity.title,
            value: [BOEScenario.loanAmount ? Number(BOEScenario.loanAmount) : 0, BOEScenario.requiredEquity ? Number(BOEScenario.requiredEquity) : 0],
            formatter: ['formatUSDCurrency', 'formatUSDCurrency'],
            rows: [
                [
                    {
                        label: placeholders.loanAndEquity.rows.constructionFinancing,
                        value: BOEScenario.loanToCost ? Number(BOEScenario.loanToCost) : null,
                        formatter: 'formatPercentage',
                    },
                ],
            ],
        },
    ],
];

export const getDisplayMode = (value: number | string | null, target: number | null): 'success' | 'danger' | 'unstyled' => {
    if (target !== null && value) {
        const numberValue = typeof value === 'number' ? value : parseFloat(value);
        return numberValue >= target ? 'success' : 'danger';
    }
    return 'unstyled';
};

export const generateScenarioReturns = (BOEScenario: BOEScenario, deal?: Deal): ScenarioReturnsSection[] => {
    const returnOnCost = BOEScenario.returnOnCost !== null && BOEScenario.returnOnCost !== undefined ? Number(BOEScenario.returnOnCost) : null;
    const targetReturnOnCost = deal && deal.targetReturnOnCost !== null ? Number(deal.targetReturnOnCost) / 100 : null;

    const returnOnEquity = BOEScenario.returnOnEquity !== null && BOEScenario.returnOnEquity !== undefined ? Number(BOEScenario.returnOnEquity) : null;
    const targetReturnOnEquity = deal && deal?.targetReturnOnEquity !== null ? Number(deal.targetReturnOnEquity) / 100 : null;

    const profitReturn = BOEScenario.profitReturn !== null && BOEScenario.profitReturn !== undefined ? Number(BOEScenario.profitReturn) : null;
    const targetProfitReturn = deal && deal?.targetProfitReturn !== null ? Number(deal.targetProfitReturn) : null;

    return [
        {
            title: [placeholders.returns.returnOnCost],
            value: [returnOnCost],
            target: targetReturnOnCost,
            formatter: ['formatPercentage'],
            displayMode: getDisplayMode(returnOnCost, targetReturnOnCost),
        },
        {
            title: [placeholders.returns.returnOnEquity],
            value: [returnOnEquity],
            target: targetReturnOnEquity,
            formatter: ['formatPercentage'],
            displayMode: getDisplayMode(returnOnEquity, targetReturnOnEquity),
        },
        {
            title: [placeholders.returns.profitReturn],
            value: [profitReturn],
            target: targetProfitReturn,
            formatter: ['formatUSDCurrency'],
            displayMode: getDisplayMode(profitReturn, targetProfitReturn),
        },
    ];
};

export const mapScenarioToFormValues = ({
    scenario,
    options,
    initialAssetTypeOption,
}: {
    scenario: BOEScenario | null;
    options: { assetTypes: IOptionAnyValue<string>[] };
    initialAssetTypeOption?: IOptionAnyValue;
}): BOESlideoutFormValues | null => {
    if (!scenario) return null;

    const currentPropertyType = scenario.propertyTypeId && options.assetTypes.find(option => Number(option.value) === scenario.propertyTypeId);
    const defaultPropertyType = options.assetTypes.find(option => Number(option.value) === ProFormaModelIds.APARTMENT);
    const selectedPropertyTypeOption = currentPropertyType || initialAssetTypeOption || defaultPropertyType;

    const selectedLoanCalculationOption = (() => {
        if (scenario.loanToValue !== null) {
            return LOAN_CALCULATION_OPTIONS.find(option => option.value === DEVELOPMENT_SOURCE_METRIC.LTV) || null;
        }
        if (scenario.loanToCost !== null) {
            return LOAN_CALCULATION_OPTIONS.find(option => option.value === DEVELOPMENT_SOURCE_METRIC.LTC) || null;
        }
        if (scenario.debtYield !== null) {
            return LOAN_CALCULATION_OPTIONS.find(option => option.value === DEVELOPMENT_SOURCE_METRIC.DEBT_YIELD) || null;
        }
        return null;
    })();

    const contructionFinancing = (() => {
        if (scenario.loanToValue !== null) {
            return parseFloat(scenario.loanToValue) * 100;
        }
        if (scenario.loanToCost !== null) {
            return parseFloat(scenario.loanToCost) * 100;
        }
        if (scenario.debtYield !== null) {
            return parseFloat(scenario.debtYield) * 100;
        }
        return null;
    })();

    const formValues: BOESlideoutFormValues = {
        id: scenario.id?.toString() || null,
        dealId: scenario.dealId || -1,
        name: scenario.name,
        propertyType: selectedPropertyTypeOption as IOptionAnyValue,
        selectedLoanCalculationOptions: selectedLoanCalculationOption,
        grossSquareFootage: scenario.builtSf ? parseFloat(scenario.builtSf) : null,
        efficiency: scenario.buildingEfficiency ? parseFloat(scenario.buildingEfficiency) * 100 : null,
        rentPrice: parseFloat(scenario.monthlyRentPricePerUnit ?? scenario.monthlyRentPricePerSf ?? '0') || null,
        expenses: parseFloat(scenario.monthlyExpenseCostPerUnit ?? scenario.monthlyExpenseCostPerSf ?? '0') || null,
        expenseCalculationMode: scenario.expenseCalculationMode,
        expensePercentOfGpr: scenario.expensePercentOfGpr ? parseFloat(scenario.expensePercentOfGpr) * 100 : null,
        units: scenario.numberOfUnits ?? null,
        exitCapRate: scenario.assumedExitCapRate ? parseFloat(scenario.assumedExitCapRate) * 100 : null,
        acquisitionCosts: scenario.acquisition ? parseFloat(scenario.acquisition) : null,
        hardCosts: parseFloat(scenario.hardCosts ?? '0') || null,
        softCosts: parseFloat(scenario.softCosts ?? '0') || null,
        developerFee: scenario.developerFeeRate ? parseFloat(scenario.developerFeeRate) * 100 : null,
        apr: scenario.annualPercentageRate ? parseFloat(scenario.annualPercentageRate) * 100 : null,
        avgLoanBalance: scenario.averageLoanBalance ? parseFloat(scenario.averageLoanBalance) * 100 : null,
        loanTerm: scenario.loanTermMonths ?? null,
        contructionFinancing,
        marketCapRate: scenario.loanToValue ? parseFloat(scenario.loanToValue) * 100 : null, // TODO Add market cap rate from API (not returned in GET)
        developerFeeTotal: scenario.developerFeeTotal ? parseFloat(scenario.developerFeeTotal) : null,
        interestReserveTotal: scenario.interestReserveTotal ? parseFloat(scenario.interestReserveTotal) : null,
        totalDevelopmentCosts: scenario.totalDevelopmentCosts ? parseFloat(scenario.totalDevelopmentCosts) : null,
        loanAmount: scenario.loanAmount ? parseFloat(scenario.loanAmount) : null,
        equityCost: scenario.requiredEquity ? parseFloat(scenario.requiredEquity) : null,
        rentPriceSubdiv: scenario.rentPriceSubdiv,
        expenseCostSubdiv: scenario.expenseCostSubdiv,
        softCostsSubdiv: scenario.softCostsSubdiv,
        hardCostsSubdiv: scenario.hardCostsSubdiv,
        rentPriceTime: scenario.rentPriceTime,
        expenseCostTime: scenario.expenseCostTime,
        loanTermTime: scenario.loanTermTime,
    };

    return formValues;
};

export const getDefaultValues = (
    {
        assetTypes,
        initialAssetType,
    }: {
        assetTypes: IOptionAnyValue<string>[];
        initialAssetType?: IOptionAnyValue
    },
): BOESlideoutFormValues => {
    const selectedAssetType = initialAssetType || assetTypes.find(option => Number(option.value) === ProFormaModelIds.APARTMENT) as IOptionAnyValue;
    const isApartmentSelected = selectedAssetType && selectedAssetType.value === ProFormaModelIds.APARTMENT;
    const isDataCenterSelected = selectedAssetType && selectedAssetType.value === ProFormaModelIds.DATA_CENTER;

    return {
        name: '',
        id: null,
        dealId: null,
        propertyType: selectedAssetType,
        selectedLoanCalculationOptions: LOAN_CALCULATION_OPTIONS[1], // LTC
        grossSquareFootage: null,
        efficiency: 85,
        rentPrice: null,
        units: null,
        expenses: null,
        expenseCalculationMode: EXPENSE_CALCULATION_MODE.UNIT_COST,
        expensePercentOfGpr: null,
        exitCapRate: 5,
        acquisitionCosts: null,
        hardCosts: null,
        softCosts: null,
        developerFee: 3,
        apr: 9,
        avgLoanBalance: 60,
        loanTerm: 2,
        contructionFinancing: 60, // LTC default
        marketCapRate: 5,
        developerFeeTotal: null, // Auto-calculated
        interestReserveTotal: null, // Auto-calculated
        totalDevelopmentCosts: null, // Auto-calculated
        loanAmount: null, // Auto-calculated
        equityCost: null, // Auto-calculated
        rentPriceSubdiv: (() => {
            if (isDataCenterSelected) return 'kw';
            if (isApartmentSelected) return 'unit';
            return 'sf';
        })() as 'unit' | 'sf' | 'kw' | 'mw',
        expenseCostSubdiv: (() => {
            if (isDataCenterSelected) return 'kw';
            if (isApartmentSelected) return 'unit';
            return 'sf';
        })() as 'unit' | 'sf' | 'kw' | 'mw',
        hardCostsSubdiv: isDataCenterSelected ? 'kw' : 'sf' as 'unit' | 'sf' | 'kw' | 'mw' | 'total',
        softCostsSubdiv: isDataCenterSelected ? 'kw' : 'sf' as 'unit' | 'sf' | 'kw' | 'mw' | 'total',
        rentPriceTime: 'monthly' as 'monthly' | 'yearly',
        expenseCostTime: 'monthly' as 'monthly' | 'yearly',
        loanTermTime: 'yearly' as 'monthly' | 'yearly',
    };
};
